import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ChevronDown, MapPin } from "lucide-react";
import { ASSETS, PHONE_DISPLAY, PHONE_TEL } from "@/lib/constants";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const scrollNext = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* Background grid + radial glow */}
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.08),transparent_55%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />

      {/* Building image - parallax */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-x-0 bottom-0 flex items-end justify-center pointer-events-none"
      >
        <img
          src={ASSETS.sticker}
          alt="Palaash Paradise building exterior"
          className="w-[88%] sm:w-[70%] md:w-[58%] lg:w-[46%] max-w-[900px] object-contain opacity-80 drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
          data-testid="hero-building-image"
        />
      </motion.div>

      {/* Floating gold orbs */}
      <div className="absolute top-1/3 left-10 w-8 h-8 rounded-full bg-[#D4AF37] opacity-60 float-y" />
      <div className="absolute top-1/4 right-16 w-10 h-10 rounded-full bg-[#F3E5AB] opacity-40 float-y" style={{ animationDelay: "1s" }} />
      <div className="absolute bottom-1/3 right-1/4 w-5 h-5 rounded-full bg-[#D4AF37] opacity-70 float-y" style={{ animationDelay: "1s" }} />

      {/* Content */}
      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-24 pb-32 min-h-screen flex flex-col"
      >
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center"
        >
          <div className="items-center gap-3 text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#D4AF37]">
            Destination of Peace & Prosperity
          </div>
        </motion.div>

        {/* Main title */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.20, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif text-center mt-4 leading-[0.95] tracking-tighter"
          data-testid="hero-title"
        >
          <span className="block text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9rem] text-[#F8F6F0]">PALAASH</span>
          <span className="block text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9rem] text-[#c9a530] italic font-medium">
            Paradise
          </span>

          

          {/* <span className="italic bg-gradient-to-r from-[#ffb413] via-[#ff744e] to-[#ff9900] text-transparent bg-clip-text">
              Growth.
            </span> */}
        </motion.h1>

        {/* Location chip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="flex items-center justify-center gap-2 mt-6 text-sm md:text-base text-[#ffffff]"
        >
          <MapPin className="w-4 h-4 text-[#ffcc25]" />
          @ Nigdi, Pradhikaran, Pune.
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8"
        >
          <a
            href={PHONE_TEL}
            data-testid="hero-call-button"
            className="btn-luxury group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#F3E5AB] text-black px-8 py-4 text-sm md:text-base font-medium tracking-[0.15em] uppercase transition-colors pulse-gold"
          >
            <Phone className="w-4 h-4 group-hover:rotate-12 transition-transform" />
            Call Now : {PHONE_DISPLAY}
          </a>
          <button
            onClick={() => document.getElementById("floor-plans")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-floorplans-button"
            className="btn-luxury inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-8 py-4 text-sm md:text-base font-medium tracking-[0.15em] uppercase transition-all"
          >
            Explore Floor Plans
          </button>
        </motion.div>

        <div className="flex-1" />

        {/* Scroll indicator */}
        {/* <motion.button
          onClick={scrollNext}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0] hover:text-[#D4AF37] transition-colors z-20"
          data-testid="hero-scroll-button"
        >
          <span>Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </motion.button> */}
      </motion.div>
    </section>
  );
};